import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import { useGetComsMonthlyReportQuery } from "../../redux/comsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import MonthlyReportsList from "./MonthlyReportsList";

const COMS_NAVY = "#123456";

interface Props {
  blockId: string;
}

const MonthlyReport: React.FC<Props> = ({ blockId }) => {
  const now = new Date();
  const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .split("T")[0];

  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(lastDay);
  const [query, setQuery] = useState<{
    blockId: string;
    from: string;
    to: string;
  } | null>(null);

  const { data: report, isLoading } = useGetComsMonthlyReportQuery(
    query ?? skipToken,
  );

  const fmt = (v: number) =>
    `$${(v ?? 0).toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <Box>
      <Typography
        variant="h2"
        sx={{ color: COMS_NAVY, fontWeight: 600, mb: 3 }}
      >
        Monthly Report
      </Typography>

      {/* ── Auto-generated monthly instances ────────────────────────────── */}
      <MonthlyReportsList blockId={blockId} />

      <Divider sx={{ my: 4 }} />

      {/* ── Ad-hoc / Period picker ────────────────────────────────────────── */}
      <Typography
        variant="h5"
        sx={{ color: COMS_NAVY, fontWeight: 700, mb: 2 }}
      >
        Ad-hoc Period Report
      </Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select date range
        </Typography>
        <Box
          display="flex"
          gap={2}
          mt={1}
          alignItems="flex-end"
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              From
            </Typography>
            <TextField
              type="date"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              sx={{ width: 160 }}
            />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              To
            </Typography>
            <TextField
              type="date"
              size="small"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              sx={{ width: 160 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CalendarMonth />
              )
            }
            onClick={() => setQuery({ blockId, from, to })}
            disabled={isLoading}
            sx={{ bgcolor: COMS_NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
          >
            Generate Report
          </Button>
        </Box>
      </Paper>

      {report && (
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ bgcolor: COMS_NAVY, px: 3, py: 2 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700 }}>
              Summary: {report.from} → {report.to}
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}
            >
              {report.reportsCount} report{report.reportsCount !== 1 ? "s" : ""}{" "}
              in range
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap">
            {[
              {
                label: "Total Revenue",
                value: report.totalRevenue,
                color: "#123456",
              },
              {
                label: "Total Materials",
                value: report.totalMaterials,
                color: "#1565c0",
              },
              {
                label: "Total Labour",
                value: report.totalLabour,
                color: "#b71c1c",
              },
              {
                label: "Total Equipment",
                value: report.totalEquipment,
                color: "#bf8c00",
              },
              {
                label: "NET PROFIT",
                value: report.totalProfit,
                color: report.totalProfit >= 0 ? "#2e7d32" : "#c62828",
              },
            ].map(({ label, value, color }) => (
              <Box
                key={label}
                sx={{ flex: "1 1 140px", px: 3, py: 3, textAlign: "center" }}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, color, fontSize: "1.25rem", mt: 0.5 }}
                >
                  {fmt(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MonthlyReport;
